import {
  Button,
  Grid2 as Grid,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import Colors from '../../../utils/colors.json'
import useCustomStyles from '../../../hooks/CustomStylesHook'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import AddIcon from '@mui/icons-material/Add'
import { COMPARISON_FIELDS, FIELD_TYPES } from '../../../constants/constants'
import dayjs from 'dayjs'

const styles = (theme: any) => ({
  sectionHeading: {
    fontSize: '16px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
  },

  fieldLabel: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_QUATERNARY,
  },

  formTextField: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    height: 40,
    borderRadius: '8px',
  },
  formSelectField: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    minWidth: '180px',
    height: 40,
    borderRadius: '8px',
  },

  actionBtn: {
    borderRadius: '20px',
    textTransform: 'capitalize',
    minWidth: '180px',
    background: theme.customColors.button,
  },
  actionBtnBack: {
    borderRadius: '20px',
    textTransform: 'capitalize',
    minWidth: '180px',
    background: theme.customColors.btnBlack,
  },
  memberContainer: {
    background: theme.palette.primary.light,
    borderRadius: '20px',
  },
})

const validationMessages = {
  fullName: {
    required: 'Full Name is required',
    min: 'Full Name must be at least 2 characters',
    max: 'Full Name cannot exceed 50 characters',
  },
  gender: {
    required: 'Gender is required',
  },
  relationship: {
    required: 'Relationship is required',
  },
  dob: {
    required: 'Date of Birth is required',
    max: 'Date of Birth cannot be in the future',
  },
  occupation: {
    required: 'Occupation is required',
  },
  height: {
    required: 'Height is required',
    positive: 'Height must be a positive number',
  },
  weight: {
    required: 'Weight is required',
    positive: 'Weight must be a positive number',
    min: 'Weight must be at least 1 kg',
    max: 'Weight cannot exceed 200 kg',
  },
  members: {
    required: 'At least one member is required',
    min: 'At least one member must be added',
  },
}

const Member = ({
  activeStep,
  setActiveStep,
}: {
  activeStep: number
  setActiveStep: (activeStep: number) => void
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const sectionData = [
    { label: 'Full Name', name: 'fullName', type: 'text' },
    {
      label: 'Gender',
      name: 'gender',
      type: 'select',
      options: [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
      ],
    },
    {
      label: 'Relationship',
      name: 'relationship',
      type: 'select',
      options: [
        { label: 'Father', value: 'father' },
        { label: 'Mother', value: 'mother' },
        { label: 'Son', value: 'son' },
        { label: 'Daughter', value: 'daughter' },
      ],
    },
    { label: 'Dob.', name: 'dob', type: 'date' },
    { label: 'Age', name: 'age', type: 'number' },
    {
      label: 'Occupation',
      name: 'occupation',
      type: 'select',
      options: [
        { label: 'Software Engineer', value: 'software_engineer' },
        { label: 'Project Manager', value: 'project_manager' },
      ],
    },
    {
      label: 'Height',
      name: 'height',
      type: 'select',
      options: Array.from({ length: (12 - 3) * 12 + 1 }, (_, i) => {
        const feet = Math.floor(i / 12) + 3 // Start at 3 feet
        const inches = i % 12

        // Convert feet and inches to centimeters
        const cm = Math.round(feet * 30.48 + inches * 2.54)

        // Return object with label and value
        return {
          label: `${feet}ft - ${inches}inch`,
          value: cm,
        }
      }),
    },
    {
      label: 'Weight',
      name: 'weight',
      type: 'select',
      options: Array.from({ length: 100 }, (_, i) => ({
        label: `${i + 1} kg`,
        value: i + 1,
      })),
    },
  ]

  const formik: any = useFormik({
    initialValues: {
      members: [
        {
          fullName: '',
          gender: '',
          relationship: '',
          dob: '',
          age: null,
          occupation: '',
          height: '',
          weight: '',
        },
      ],
    },
    validationSchema: Yup.object({
      members: Yup.array()
        .of(
          Yup.object().shape({
            fullName: Yup.string()
              .trim()
              .required(validationMessages.fullName.required)
              .min(2, validationMessages.fullName.min)
              .max(50, validationMessages.fullName.max),
            gender: Yup.string().required(validationMessages.gender.required),
            relationship: Yup.string().required(
              validationMessages.relationship.required
            ),
            dob: Yup.date()
              .required(validationMessages.dob.required)
              .max(new Date(), validationMessages.dob.max),
            occupation: Yup.string().required(
              validationMessages.occupation.required
            ),
            height: Yup.number()
              .required(validationMessages.height.required)
              .positive(validationMessages.height.positive),
            weight: Yup.number()
              .required(validationMessages.weight.required)
              .positive(validationMessages.weight.positive)
              .min(1, validationMessages.weight.min)
              .max(200, validationMessages.weight.max),
          })
        )
        .required(validationMessages.members.required)
        .min(1, validationMessages.members.min),
    }),
    onSubmit: (values) => {
      console.log(values)
      alert('Form submitted successfully!')
    },
  })

  const addMember = () => {
    formik.setFieldValue('members', [
      ...formik.values.members,
      {
        fullName: '',
        gender: '',
        relationship: '',
        dob: '',
        age: null,
        occupation: '',
        height: '',
        weight: '',
      },
    ])
  }

  const calculateAge = (dob: string) => {
    const birthDate = dayjs(dob)
    const today = dayjs()

    // Calculate age by subtracting the years
    let age = today.year() - birthDate.year()

    // Adjust age if the birthday hasn't occurred yet this year
    if (today.isBefore(birthDate.add(age, 'year'))) {
      age -= 1
    }

    return age
  }

  const calculateDob = (age: number) => {
    const today = dayjs()
    const birthYear = today.year() - age
    const birthDate = today.set('year', birthYear)

    // Adjust DOB if the birthday hasn't occurred yet this year
    if (today.isBefore(birthDate)) {
      return birthDate.subtract(1, 'year').format('YYYY-MM-DD')
    }
    console.log()
    return birthDate.format('YYYY-MM-DD')
  }

  const handleSectionChange = (e: any, memberIndex: number) => {
    const fieldName = e.target.name
    const fieldValue = e.target.value

    // Custom logic for specific fields
    if (fieldName.includes(COMPARISON_FIELDS.DOB)) {
      formik.handleChange(e)
      const age = calculateAge(fieldValue)
      formik.setFieldValue(
        `members[${memberIndex}].${COMPARISON_FIELDS.AGE}`,
        age
      )
    } else if (fieldName.includes(COMPARISON_FIELDS.AGE)) {
      formik.handleChange(e)
      const dob = calculateDob(fieldValue)
      formik.setFieldValue(
        `members[${memberIndex}].${COMPARISON_FIELDS.DOB}`,
        dob
      )
    } else {
      formik.handleChange(e) // Default behavior
    }
  }

  return (
    <Grid container direction={'column'} spacing={4} pl={2} pb={4}>
      <Grid container direction={'row'} spacing={3}>
        <Typography>Select Members you want to Insure</Typography>
        <Button
          variant='contained'
          color='primary'
          startIcon={<AddIcon />}
          onClick={addMember}
        >
          {' '}
          Add Members
        </Button>
      </Grid>

      <Grid size='auto'>
        <Typography className={classes?.sectionHeading}>
          Tell us Details About The Members To Be Insured
        </Typography>
      </Grid>
      <Grid container spacing={2.5}>
        <Grid container spacing={10} size={{ xs: 12 }}>
          {formik?.values?.members?.map((member: any, memberIndex: number) => (
            <Grid
              container
              spacing={3}
              key={memberIndex}
              className={classes?.memberContainer}
              p={2}
            >
              {sectionData?.map((item, index) => (
                <Grid
                  container
                  direction={'column'}
                  size={{ xs: 6, sm: 6, lg: 4, md: 4, xl: 4 }}
                  spacing={1}
                  key={`${memberIndex}-${index}`}
                >
                  <Grid size='auto'>
                    <Typography className={classes?.fieldLabel}>
                      {item?.label}
                    </Typography>
                  </Grid>
                  <Grid size='auto'>
                    <TextField
                      name={`members[${memberIndex}].${item.name}`}
                      value={
                        formik?.values?.members?.[memberIndex]?.[item.name] ||
                        ''
                      }
                      onChange={(e) => handleSectionChange(e, memberIndex)}
                      onBlur={formik.handleBlur}
                      error={
                        formik?.touched?.members?.[memberIndex]?.[item?.name] &&
                        Boolean(
                          formik?.errors?.members?.[memberIndex]?.[item?.name]
                        )
                      }
                      helperText={
                        formik?.touched?.members?.[memberIndex]?.[item?.name] &&
                        formik?.errors?.members?.[memberIndex]?.[item?.name]
                      }
                      slotProps={{
                        input: {
                          className:
                            item?.type === FIELD_TYPES?.SELECT
                              ? classes?.formSelectField
                              : classes?.formTextField,
                        },
                      }}
                      fullWidth
                      type={
                        item?.type !== FIELD_TYPES?.SELECT
                          ? item?.type
                          : undefined
                      }
                      select={item?.type === FIELD_TYPES?.SELECT}
                    >
                      {item?.type === FIELD_TYPES?.SELECT &&
                        item?.options?.map((option, optionIndex) => (
                          <MenuItem key={optionIndex} value={option?.value}>
                            {option?.label}
                          </MenuItem>
                        ))}
                    </TextField>
                    {item?.name === COMPARISON_FIELDS.HEIGHT && (
                      <TextField
                        type='number'
                        name={`members[${memberIndex}].${item.name}`}
                        onChange={formik.handleChange}
                        value={
                          formik?.values?.members?.[memberIndex]?.[item.name]
                        }
                        sx={{ mt: 1, float: 'right' }}
                        placeholder='Enter Height In Cms'
                        slotProps={{
                          input: {
                            className: classes?.formTextField,
                            endAdornment: 'Cm',
                          },
                        }}
                      />
                    )}
                    {/* {item?.name === COMPARISON_FIELDS.WEIGHT && (
                      <TextField
                        type='number'
                        name={`members[${memberIndex}].${item.name}`}
                        onChange={formik.handleChange}
                        value={
                          formik?.values?.members?.[memberIndex]?.[item.name]
                        }
                        sx={{ mt: 1, float: 'right' }}
                        placeholder='Enter Weight'
                        slotProps={{
                          input: {
                            className: classes?.formTextField,
                            endAdornment: 'KG',
                          },
                        }}
                      />
                    )} */}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          ))}{' '}
        </Grid>
      </Grid>
      <Grid
        size={{ xs: 12 }}
        textAlign={'center'}
        display={'flex'}
        justifyContent={'space-around'}
      >
        <Button
          variant='contained'
          // color='primary'
          className={classes?.actionBtnBack}
          onClick={() => setActiveStep(activeStep - 1)}
        >
          Back
        </Button>
        <Button
          variant='contained'
          // color='primary'
          className={classes?.actionBtn}
          onClick={() => setActiveStep(activeStep + 1)}
        >
          Proceed To Medical Questions
        </Button>
      </Grid>
    </Grid>
  )
}

export default Member
